import React, { useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  Collapse,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import TaskAltIcon from '@mui/icons-material/TaskAlt';

const STATUS_META = {
  matched: { label: 'Matched', color: 'success', icon: <TaskAltIcon fontSize="small" /> },
  needs_review: { label: 'Needs review', color: 'warning', icon: <HelpOutlineIcon fontSize="small" /> },
  brand_conflict: { label: 'Brand conflict', color: 'error', icon: <ErrorOutlineIcon fontSize="small" /> },
};

const methodLabel = (value) => String(value || 'manual_review')
  .replace(/_/g, ' ')
  .replace(/\b\w/g, (letter) => letter.toUpperCase());

const summaryText = (match) => {
  if (match.salesforceAccountName || match.salesforceAccountId) {
    return `${match.salesforceAccountName || 'Salesforce account'}${match.salesforceAccountId ? ` (${match.salesforceAccountId})` : ''}`;
  }
  return match.reason || 'No account selected';
};

const compactCandidates = (candidates = []) => candidates
  .slice(0, 3)
  .map((candidate) => candidate.accountName || candidate.accountId || candidate.orderItemId)
  .filter(Boolean);

const AccountMatchRow = ({ match }) => {
  const meta = STATUS_META[match.matchStatus] || STATUS_META.needs_review;
  const candidates = compactCandidates(match.candidates);
  return (
    <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 1 }}>
      <Stack spacing={0.75}>
        <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
            <Chip size="small" color={meta.color} icon={meta.icon} label={meta.label} variant="outlined" />
            <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 800 }}>
              {match.productCode || 'Unknown product'}
            </Typography>
          </Stack>
          <Chip size="small" label={`${match.confidence || 0}%`} variant="outlined" sx={{ height: 22 }} />
        </Stack>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
            {summaryText(match)}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {methodLabel(match.matchMethod)}
            {match.orderItemId ? ` - order item ${match.orderItemId}` : ''}
            {match.referenceId ? ` - ref ${match.referenceId}` : ''}
          </Typography>
        </Box>
        {match.reason && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {match.reason}
          </Typography>
        )}
        {candidates.length > 0 && match.matchStatus !== 'matched' && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Candidates: {candidates.join(', ')}
          </Typography>
        )}
      </Stack>
    </Paper>
  );
};

const AccountMatchPanel = ({ matches = [], defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);

  if (!matches.length) {
    return (
      <Alert severity="info" icon={<AccountTreeIcon fontSize="small" />} sx={{ mt: 1 }}>
        No stored Salesforce account match evidence is available for this BAC yet.
      </Alert>
    );
  }

  const needsReview = matches.filter((match) => match.matchStatus !== 'matched').length;
  return (
    <Stack spacing={1}>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setOpen((current) => !current);
          }
        }}
        sx={{
          flexWrap: 'wrap',
          gap: 0.5,
          cursor: 'pointer',
          py: 0.5,
          px: 0.5,
          mx: -0.5,
          borderRadius: 0.75,
          transition: 'background-color 100ms, color 100ms',
          '&:hover': { color: 'primary.main', bgcolor: 'action.hover' },
          '&:focus-visible': {
            outline: '2px solid',
            outlineColor: 'primary.main',
            outlineOffset: 1,
          },
        }}
      >
        <KeyboardArrowDownIcon
          fontSize="small"
          sx={{
            color: 'text.secondary',
            transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
            transition: 'transform 160ms cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        />
        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
          Salesforce account matching
        </Typography>
        <Chip size="small" color={needsReview ? 'warning' : 'success'} label={`${matches.length - needsReview}/${matches.length} matched`} />
        {needsReview > 0 && <Chip size="small" color="warning" variant="outlined" label={`${needsReview} need review`} />}
      </Stack>
      <Collapse in={open} timeout={160}>
        <Stack spacing={1} sx={{ pl: 2.5 }}>
          {matches.map((match) => (
            <AccountMatchRow key={match.id || `${match.productCode}-${match.gmRowId}`} match={match} />
          ))}
        </Stack>
      </Collapse>
    </Stack>
  );
};

export default AccountMatchPanel;
